import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../utils/axiosInstance";
import { useModal } from "../context/ModalContext";
import {
  FaUsers, FaFileExcel, FaSearch, FaWhatsapp, FaArrowLeft,
  FaCrown, FaCopy, FaDownload,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  Card, CardHeader, CardBody, PageHeader, Button, Spinner,
  TableWrapper, Table, Th, Td, EmptyState, Select,
  InputGroup,
} from "../components/ui";

export default function GroupExport() {
  const modal = useModal();

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  // Groups
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");

  // Members (selected group)
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  // Fetch sessions
  useEffect(() => {
    api.get("/api/sessions")
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        const connected = list.filter((s) => s.status === "connected");
        setSessions(connected);
        if (connected.length > 0) setSessionId(connected[0].sessionId);
      })
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  // Fetch groups when session changes
  const fetchGroups = useCallback(async () => {
    if (!sessionId) return;
    setGroupsLoading(true);
    setSelectedGroup(null);
    setMembers([]);
    try {
      const res = await api.get(`/api/groups/${sessionId}`);
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      setGroups(data);
    } catch {
      modal.alert("Failed to load groups. Make sure the session is connected.", "error");
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) fetchGroups();
  }, [sessionId, fetchGroups]);

  // Fetch members for a group
  const fetchMembers = async (group) => {
    setSelectedGroup(group);
    setMembersLoading(true);
    setMemberSearch("");
    try {
      const res = await api.get(`/api/groups/${sessionId}/${group.id}/members`);
      setMembers(res.data.members || []);
    } catch {
      modal.alert("Failed to fetch group members.", "error");
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  // Back to groups list
  const backToGroups = () => {
    setSelectedGroup(null);
    setMembers([]);
    setMemberSearch("");
  };

  // Export members to Excel
  const exportToExcel = async () => {
    if (members.length === 0) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Members");
    ws.columns = [
      { header: "#", key: "num", width: 5 },
      { header: "Phone Number", key: "phone", width: 20 },
      { header: "Admin", key: "admin", width: 10 },
      { header: "Status", key: "status", width: 18 },
    ];
    members.forEach((m, i) => ws.addRow({
      num: i + 1,
      phone: m.number,
      admin: m.isAdmin ? "Yes" : "No",
      status: m.isLid ? "Unresolved LID" : "OK",
    }));
    const buffer = await wb.xlsx.writeBuffer();
    const groupName = (selectedGroup?.name || "group").replace(/[^a-zA-Z0-9]/g, "_");
    saveAs(new Blob([buffer]), `${groupName}-members.xlsx`);
  };

  // Export all groups summary
  const exportGroupsSummary = async () => {
    if (groups.length === 0) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Groups");
    ws.columns = [
      { header: "#", key: "num", width: 5 },
      { header: "Group Name", key: "name", width: 30 },
      { header: "Members", key: "size", width: 10 },
      { header: "Group ID", key: "id", width: 30 },
    ];
    groups.forEach((g, i) => ws.addRow({ num: i + 1, name: g.name, size: g.size, id: g.id }));
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `walytic-groups-${sessionId}.xlsx`);
  };

  // Copy all resolved numbers (skip unresolved LIDs)
  const copyAllNumbers = () => {
    const numbers = resolvedMembers.map((m) => m.number).join("\n");
    navigator.clipboard.writeText(numbers);
    modal.alert(`${resolvedMembers.length} numbers copied to clipboard${lidCount > 0 ? ` (${lidCount} unresolved skipped)` : ""}`, "success");
  };

  // Filtered lists
  const filteredGroups = groups.filter((g) =>
    (g.name || "").toLowerCase().includes(groupSearch.toLowerCase())
  );

  const filteredMembers = members.filter((m) =>
    (m.number || "").includes(memberSearch)
  );

  const adminCount = members.filter((m) => m.isAdmin).length;
  const lidCount = members.filter((m) => m.isLid).length;
  const resolvedMembers = members.filter((m) => !m.isLid);

  return (
    <div className="page-content">
      <PageHeader
        title="Group Members Export"
        subtitle="View WhatsApp groups and export member lists to Excel"
        guideUrl="/guide/group-export"
      />

      {/* Session Selector */}
      <Card className="mb-5">
        <CardBody>
          <InputGroup label="WhatsApp Session">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-ink-muted text-sm py-2">
                <Spinner size="sm" /> Loading sessions...
              </div>
            ) : sessions.length > 0 ? (
              <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                {sessions.map((s) => (
                  <option key={s.sessionId} value={s.sessionId}>
                    {s.phone || s.sessionId} — {s.name || "WhatsApp Session"}
                  </option>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink-muted py-2">
                <FaWhatsapp className="text-ink-faint" />
                No connected sessions. Connect one from WhatsApp Phones.
              </div>
            )}
          </InputGroup>
        </CardBody>
      </Card>

      {/* Main Content */}
      {!selectedGroup ? (
        /* ===== Groups List View ===== */
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaUsers size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">
                Groups {groups.length > 0 && `(${groups.length})`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {groups.length > 0 && (
                <Button variant="secondary" size="sm" onClick={exportGroupsSummary}>
                  <FaDownload size={10} className="mr-1.5" /> Export List
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={fetchGroups} loading={groupsLoading}>
                Refresh
              </Button>
            </div>
          </CardHeader>

          {/* Search */}
          {groups.length > 0 && (
            <div className="px-5 pt-3">
              <div className="relative">
                <FaSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint z-10" />
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  className="input"
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>
          )}

          {groupsLoading ? (
            <CardBody>
              <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>
            </CardBody>
          ) : filteredGroups.length > 0 ? (
            <div className="divide-y divide-surface-border mt-2">
              {filteredGroups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => fetchMembers(g)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-bg transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
                      <FaUsers size={14} className="text-brand" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-primary truncate">{g.name}</p>
                      <p className="text-xs text-ink-muted">{g.size} members</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {g.isCommunity && <span className="badge-info text-2xs">Community</span>}
                    {g.isAnnounce && <span className="badge-warning text-2xs">Announce</span>}
                    <span className="text-ink-faint text-xs">→</span>
                  </div>
                </button>
              ))}
            </div>
          ) : groups.length > 0 ? (
            <CardBody>
              <EmptyState
                icon={FaSearch}
                title="No groups match"
                description={`No groups found matching "${groupSearch}"`}
              />
            </CardBody>
          ) : (
            <CardBody>
              <EmptyState
                icon={FaUsers}
                title="No groups found"
                description="Select a connected session to view your WhatsApp groups."
              />
            </CardBody>
          )}
        </Card>
      ) : (
        /* ===== Members View ===== */
        <div className="space-y-5">
          {/* Group header */}
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={backToGroups} className="btn-icon">
                    <FaArrowLeft size={12} />
                  </button>
                  <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
                    <FaUsers size={16} className="text-brand" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-ink-primary">{selectedGroup.name}</h3>
                    <p className="text-xs text-ink-muted">
                      {members.length} members · {adminCount} admin{adminCount !== 1 ? "s" : ""}
                      {lidCount > 0 && <span className="text-amber-500"> · {lidCount} unresolved</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={copyAllNumbers} disabled={members.length === 0}>
                    <FaCopy size={10} className="mr-1.5" /> Copy Numbers
                  </Button>
                  <Button variant="primary" size="sm" onClick={exportToExcel} disabled={members.length === 0}>
                    <FaFileExcel size={10} className="mr-1.5" /> Export Excel
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* LID warning */}
          {!membersLoading && lidCount > 0 && (
            <div className="alert-warning flex items-start gap-2.5 text-xs">
              <FaExclamationTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>{lidCount} member{lidCount !== 1 ? "s" : ""}</strong> could not be resolved to real phone numbers.
                These are internal WhatsApp LIDs — they get resolved automatically once you exchange messages with these contacts.
                Unresolved numbers are skipped when copying.
              </div>
            </div>
          )}

          {/* Members table */}
          <Card>
            <CardHeader className="flex items-center justify-between overflow-visible">
              <span className="font-semibold text-ink-primary text-sm">
                Members {members.length > 0 && `(${members.length})`}
              </span>
              {members.length > 0 && (
                <div className="relative w-48 flex-shrink-0">
                  <FaSearch size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint z-10" />
                  <input
                    type="text"
                    placeholder="Search number..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="input text-xs w-full"
                    style={{ paddingLeft: 32 }}
                  />
                </div>
              )}
            </CardHeader>

            {membersLoading ? (
              <CardBody>
                <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>
              </CardBody>
            ) : filteredMembers.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-12">#</Th>
                      <Th>Phone Number</Th>
                      <Th>Role</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m, i) => (
                      <tr key={i} className={m.isLid ? "bg-amber-50" : ""}>
                        <Td className="text-ink-faint text-xs">{i + 1}</Td>
                        <Td className="font-mono text-sm">
                          <div className="flex items-center gap-2">
                            {m.number}
                            {m.isLid && (
                              <span className="badge-warning flex items-center gap-1 text-2xs" title="This is an internal WhatsApp LID, not a real phone number. The number could not be resolved.">
                                <FaExclamationTriangle size={8} /> Unresolved
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td>
                          {m.isAdmin ? (
                            <span className="badge-brand flex items-center gap-1 w-fit">
                              <FaCrown size={9} /> Admin
                            </span>
                          ) : (
                            <span className="text-xs text-ink-muted">Member</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : members.length > 0 ? (
              <CardBody>
                <EmptyState
                  icon={FaSearch}
                  title="No matches"
                  description={`No members matching "${memberSearch}"`}
                />
              </CardBody>
            ) : (
              <CardBody>
                <EmptyState
                  icon={FaUsers}
                  title="No members found"
                  description="Could not retrieve members for this group."
                />
              </CardBody>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
